import React, { useMemo, useState } from "react";
import "./BreakevenPage.css";

const PAYROLL_TAX_RATE = 7.62 / 100;

const defaultFixedCosts = [
  { name: "Rent", amount: "" },
  { name: "Electricity", amount: "" },
  { name: "Gas", amount: "" },
  { name: "Water", amount: "" },
  { name: "Garbage", amount: "" },
  { name: "Phone", amount: "" },
  { name: "Internet", amount: "" },
  { name: "Insurance", amount: "" },
  { name: "Software / Subscriptions", amount: "" },
  { name: "Maintenance", amount: "" },
  { name: "Repairs", amount: "" },
  { name: "Stationary", amount: "" },
  { name: "Miscellaneous", amount: "" },
];

const BreakevenPage = () => {
  const [percentageCosts, setPercentageCosts] = useState([
    { name: "Cost of Goods", percent: "" },
    { name: "Royalty / Franchise Fees", percent: "" },
    { name: "Merchant Fees", percent: "" },
  ]);

  const [fixedCosts, setFixedCosts] = useState(defaultFixedCosts);

  const [managerType, setManagerType] = useState("hourly");
  const [totalOperatingHours, setTotalOperatingHours] = useState("");
  const [managerHours, setManagerHours] = useState("");
  const [managerHourlyRate, setManagerHourlyRate] = useState("");
  const [managerWeeklySalary, setManagerWeeklySalary] = useState("");
  const [employeeHourlyRate, setEmployeeHourlyRate] = useState("");

  const toNumber = (value) => Number(value) || 0;

  const updatePercentageCost = (index, field, value) => {
    setPercentageCosts((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addPercentageCost = () => {
    setPercentageCosts((prev) => [...prev, { name: "", percent: "" }]);
  };

  const removePercentageCost = (index) => {
    setPercentageCosts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFixedCost = (index, field, value) => {
    setFixedCosts((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addFixedCost = () => {
    setFixedCosts((prev) => [...prev, { name: "", amount: "" }]);
  };

  const removeFixedCost = (index) => {
    setFixedCosts((prev) => prev.filter((_, i) => i !== index));
  };

  const calculations = useMemo(() => {
    const totalVariablePercent = percentageCosts.reduce(
      (sum, item) => sum + toNumber(item.percent),
      0
    );

    const grossMarginPercent = 100 - totalVariablePercent;
    const grossMarginDecimal = grossMarginPercent / 100;

    const totalMonthlyFixedCost = fixedCosts.reduce(
      (sum, item) => sum + toNumber(item.amount),
      0
    );

    const weeklyFixedCost = (totalMonthlyFixedCost * 12) / 52;

    const managerWeeklyCost =
      managerType === "salary"
        ? toNumber(managerWeeklySalary)
        : toNumber(managerHourlyRate) * toNumber(managerHours);

    const employeeHours = Math.max(
      toNumber(totalOperatingHours) - toNumber(managerHours),
      0
    );

    const employeeWeeklyCost = employeeHours * toNumber(employeeHourlyRate);

    const payrollBeforeTax = managerWeeklyCost + employeeWeeklyCost;

    const payrollTax = payrollBeforeTax * PAYROLL_TAX_RATE;

    const totalWeeklyLaborCost =
      managerWeeklyCost + employeeWeeklyCost + payrollTax;

    const totalWeeklyFixedCost = weeklyFixedCost + totalWeeklyLaborCost;

    const breakevenWeeklySales =
      grossMarginDecimal > 0
        ? totalWeeklyFixedCost / grossMarginDecimal
        : 0;

    return {
      totalVariablePercent,
      grossMarginPercent,
      totalMonthlyFixedCost,
      weeklyFixedCost,
      managerWeeklyCost,
      employeeHours,
      employeeWeeklyCost,
      payrollTax,
      totalWeeklyLaborCost,
      totalWeeklyFixedCost,
      breakevenWeeklySales,
    };
  }, [
    percentageCosts,
    fixedCosts,
    managerType,
    totalOperatingHours,
    managerHours,
    managerHourlyRate,
    managerWeeklySalary,
    employeeHourlyRate,
  ]);

  const formatCurrency = (value) =>
    `$${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <div className="breakeven-page">
    <div className="breakeven-topbar">
  <a href="/" className="breakeven-back-btn">
    ← Back to Home
  </a>
</div>
      <div className="breakeven-header">
        <div>
          <h1>Breakeven Calculator</h1>
          <p>Find the weekly sales needed to cover labor, fixed costs, and variable costs.</p>
        </div>

        <div className="breakeven-badge">Weekly</div>
      </div>

      <div className="breakeven-layout">
        <div className="breakeven-left">
          <section className="breakeven-card">
            <div className="breakeven-section-title">
              <h2>Percentage Costs</h2>
              <span>% of sales</span>
            </div>

            {percentageCosts.map((item, index) => (
              <div className="breakeven-row" key={index}>
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) =>
                    updatePercentageCost(index, "name", e.target.value)
                  }
                  placeholder="Cost name"
                />

                <div className="breakeven-percent-input">
                  <input
                    type="number"
                    value={item.percent}
                    onChange={(e) =>
                      updatePercentageCost(index, "percent", e.target.value)
                    }
                    placeholder="0"
                  />
                  <span>%</span>
                </div>

                {index > 2 && (
                  <button
                    type="button"
                    className="breakeven-remove-btn"
                    onClick={() => removePercentageCost(index)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              className="breakeven-add-btn"
              onClick={addPercentageCost}
            >
              + Add Percentage Cost
            </button>
          </section>

          <section className="breakeven-card">
            <div className="breakeven-section-title">
              <h2>Labor</h2>
              <span>weekly</span>
            </div>

            <div className="breakeven-grid">
              <div className="breakeven-field">
                <label>Total Operating Hours / Week</label>
                <input
                  type="number"
                  value={totalOperatingHours}
                  onChange={(e) => setTotalOperatingHours(e.target.value)}
                  placeholder="Example: 98"
                />
              </div>

              <div className="breakeven-field">
                <label>Manager Type</label>
                <select
                  value={managerType}
                  onChange={(e) => setManagerType(e.target.value)}
                >
                  <option value="hourly">Hourly</option>
                  <option value="salary">Salary</option>
                </select>
              </div>

              <div className="breakeven-field">
                <label>Manager Hours / Week</label>
                <input
                  type="number"
                  value={managerHours}
                  onChange={(e) => setManagerHours(e.target.value)}
                  placeholder="Example: 40"
                />
              </div>

              {managerType === "hourly" ? (
                <div className="breakeven-field">
                  <label>Manager Hourly Rate</label>
                  <input
                    type="number"
                    value={managerHourlyRate}
                    onChange={(e) => setManagerHourlyRate(e.target.value)}
                    placeholder="Example: 22"
                  />
                </div>
              ) : (
                <div className="breakeven-field">
                  <label>Manager Weekly Salary</label>
                  <input
                    type="number"
                    value={managerWeeklySalary}
                    onChange={(e) => setManagerWeeklySalary(e.target.value)}
                    placeholder="Example: 950"
                  />
                </div>
              )}

              <div className="breakeven-field">
                <label>Average Employee Hourly Rate</label>
                <input
                  type="number"
                  value={employeeHourlyRate}
                  onChange={(e) => setEmployeeHourlyRate(e.target.value)}
                  placeholder="Example: 15"
                />
              </div>

              <div className="breakeven-field breakeven-readonly">
                <label>Employee Hours / Week</label>
                <input
                  type="text"
                  value={calculations.employeeHours.toFixed(2)}
                  readOnly
                />
              </div>
            </div>
          </section>

          <section className="breakeven-card">
            <div className="breakeven-section-title">
              <h2>Monthly Fixed Costs</h2>
              <span>converted to weekly</span>
            </div>

            {fixedCosts.map((item, index) => (
              <div className="breakeven-row" key={index}>
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) =>
                    updateFixedCost(index, "name", e.target.value)
                  }
                  placeholder="Cost name"
                />

                <input
                  type="number"
                  value={item.amount}
                  onChange={(e) =>
                    updateFixedCost(index, "amount", e.target.value)
                  }
                  placeholder="Monthly amount"
                />

                {index >= defaultFixedCosts.length && (
                  <button
                    type="button"
                    className="breakeven-remove-btn"
                    onClick={() => removeFixedCost(index)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              className="breakeven-add-btn"
              onClick={addFixedCost}
            >
              + Add Fixed Cost
            </button>
          </section>
        </div>

        <div className="breakeven-right">
          <section className="breakeven-summary-card">
            <h2>Weekly Breakeven Sales</h2>

            <div className="breakeven-main-number">
              {formatCurrency(calculations.breakevenWeeklySales)}
            </div>

            <p className="breakeven-summary-note">
              This is the estimated weekly sales amount needed to break even.
            </p>

            <div className="breakeven-summary-list">
              <div>
                <span>Total Variable Cost</span>
                <strong>{calculations.totalVariablePercent.toFixed(2)}%</strong>
              </div>

              <div>
                <span>Gross Margin</span>
                <strong>{calculations.grossMarginPercent.toFixed(2)}%</strong>
              </div>

              <div>
                <span>Monthly Fixed Costs</span>
                <strong>{formatCurrency(calculations.totalMonthlyFixedCost)}</strong>
              </div>

              <div>
                <span>Weekly Fixed Costs</span>
                <strong>{formatCurrency(calculations.weeklyFixedCost)}</strong>
              </div>

              <div>
                <span>Manager Cost</span>
                <strong>{formatCurrency(calculations.managerWeeklyCost)}</strong>
              </div>

              <div>
                <span>Employee Cost</span>
                <strong>{formatCurrency(calculations.employeeWeeklyCost)}</strong>
              </div>

              <div>
                <span>Payroll Tax 7.62%</span>
                <strong>{formatCurrency(calculations.payrollTax)}</strong>
              </div>

              <div>
                <span>Total Labor Cost</span>
                <strong>{formatCurrency(calculations.totalWeeklyLaborCost)}</strong>
              </div>

              <div className="breakeven-total-row">
                <span>Total Weekly Cost Before Variable Costs</span>
                <strong>{formatCurrency(calculations.totalWeeklyFixedCost)}</strong>
              </div>
            </div>

            {calculations.grossMarginPercent <= 0 && (
              <div className="breakeven-warning">
                Variable costs must be less than 100% to calculate breakeven sales.
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default BreakevenPage;